"use client";

import { useEffect, useMemo, useState } from "react";
import type { BoxCode, Variety } from "@/lib/catalog/types";
import type { Card } from "@/lib/catalog/grouping";
import {
  boxCapacityForLength,
  effectiveDefaultLength,
  pricePerStem as pricePerStemAt,
  stemsInBox,
} from "@/lib/catalog/pricing";
import { getDefaultBoxUnit } from "@/lib/catalog/box-unit-defaults";
import { addToBox } from "@/lib/cart/cart";
import { readCart, writeCart } from "@/lib/cart/storage";
import { readVarietyPref, writeVarietyPref } from "@/lib/catalog/preferences";
import { usePriceGate } from "@/components/auth/usePriceGate";
import { SignInForPricesCTA } from "./SignInForPricesCTA";
import { useRouter } from "next/navigation";
import { usePreferences } from "@/stores/preferences";
import type { PlainCatalogStrings } from "@/lib/i18n/catalog-strings";
import { ColorPicker, type ColorChoice } from "./ColorPicker";
import { mixAllowedForUnit, leadColor } from "@/lib/catalog/color-selection";
import { isSoldOut } from "@/lib/availability/keys";

const ALL_BOX_CODES: BoxCode[] = ["EB", "QB", "HB"];

function resolveStemPrice(
  variety: Variety,
  length: number,
  colorChoice?: string,
): number {
  const fallback = pricePerStemAt(variety, length) ?? 0;
  if (!colorChoice || !variety.priceByColor) return fallback;
  if (colorChoice === "mix") {
    const all = Object.values(variety.priceByColor);
    if (all.length === 0) return fallback;
    return Math.min(...all);
  }
  return variety.priceByColor[colorChoice] ?? fallback;
}

function colorLabel(c: string): string {
  return c
    .split("-")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

export function QuickAddPopover({
  card,
  open,
  onClose,
  onAdded,
  strings,
  presetBunches,
  presetLength,
  presetColor,
  soldOutKeys,
}: {
  card: Card;
  open: boolean;
  onClose: () => void;
  onAdded?: (msg: string) => void;
  strings?: PlainCatalogStrings;
  // #452: active sold-out keys. Greys the sold-out length chip + blocks the
  // confirm button when the current variety/color/length selection is out.
  soldOutKeys?: string[];
  // DIRECTIVE #020 (Fix 2): allow the cart's underfill banner to open
  // this popover with the bunch count + length + color pre-filled so
  // the buyer can confirm with a single click.
  presetBunches?: number;
  presetLength?: number;
  presetColor?: string;
}) {
  const [variantIdx, setVariantIdx] = useState(0);
  const variety = card.variants[Math.min(variantIdx, card.variants.length - 1)];
  const isHydrangea = variety.purchaseUnit === "box";
  // Step 6.7: only roses get a length picker. Specialty/filler/garden roses
  // have a `lengths` field but no length-driven price or capacity, so the
  // chips are noise (e.g. Asiatic Lily was rendering "50 60" chips).
  const showLengthPicker =
    variety.category === "rose" &&
    variety.lengths !== undefined &&
    variety.lengths.length > 1;
  const hasColorPicker =
    Array.isArray(variety.colorOptions) && variety.colorOptions.length > 0;

  // #468 (Alexis): by-bunch default = the variety's LEAD color, never Mix (Mix
  // is a by-box option only). Default length = 60 (or smallest); default unit =
  // the global Buy-by pref.
  const initialColor: ColorChoice = hasColorPicker
    ? (leadColor(variety) as ColorChoice)
    : "";
  const [color, setColor] = useState<ColorChoice>(initialColor);
  const [length, setLength] = useState<number>(effectiveDefaultLength(variety));
  // Step 6.7.3.1 / AC184: Buy-By is a view of the global usePreferences
  // store, same store as the catalog grid / Power Buyer / detail page.
  // Bunch chosen anywhere persists everywhere.
  const globalUnit = usePreferences((s) => s.unitPreference);
  const setGlobalUnit = usePreferences((s) => s.setUnitPreference);
  // Hydrangea is always Box regardless of the global preference.
  const unit: "bunch" | "box" = isHydrangea ? "box" : globalUnit;
  // #468 (Alexis): Mix is by-box ONLY. If Mix is selected while buying by bunch
  // (e.g. picked Mix in box mode then toggled to Bunch), fall back to the lead
  // color so a single bunch is never "Mix".
  useEffect(() => {
    if (unit === "bunch" && color === "mix") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setColor(leadColor(variety) as ColorChoice);
    }
  }, [unit, color, variety]);
  const [quantity, setQuantity] = useState<number>(1);

  const cap = useMemo(
    () => boxCapacityForLength(variety, length),
    [variety, length],
  );
  const availableBoxCodes = useMemo<BoxCode[]>(
    () =>
      cap ? ALL_BOX_CODES.filter((c) => cap[c] !== null) : [],
    [cap],
  );
  const [boxCode, setBoxCode] = useState<BoxCode | null>(() => {
    if (!cap) return null;
    // DIRECTIVE #023: universal QB default with per-variety memory. The
    // saved boxCode (from a prior explicit pick) wins; otherwise pick QB
    // where available.
    const saved = typeof window !== "undefined" ? readVarietyPref(variety.slug) : null;
    return getDefaultBoxUnit(variety, saved?.boxCode ?? null);
  });

  // Reset state when the popover re-opens or the variant changes.
  // Step 6.7 section 7c: saved per-variety prefs override spec defaults.
  /* eslint-disable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps -- one-shot reset on open / variant change */
  useEffect(() => {
    if (!open) return;
    const saved = readVarietyPref(variety.slug);
    // DIRECTIVE #020: preset props win over saved-pref + spec defaults
    // so the cart-banner "Add 50 stems" click lands the popover at the
    // exact bunches/length/color the banner promised.
    setColor(
      (presetColor as ColorChoice | undefined) ??
        saved?.color ??
        // RB-1 (2026-10-12): default to the LEAD color (#468 rule, Sep 10), not
        // the first option - this open-reset was overwriting initialColor.
        (hasColorPicker ? (leadColor(variety) as ColorChoice) : ""),
    );
    const presetLenValid =
      typeof presetLength === "number" && variety.lengths.includes(presetLength);
    setLength(
      presetLenValid
        ? (presetLength as number)
        : saved?.length && variety.lengths.includes(saved.length)
          ? saved.length
          : effectiveDefaultLength(variety),
    );
    // 12.8: a per-variety saved unit must NOT hijack the GLOBAL box/bunch
    // toggle. It used to: opening one variety saved as "bunch" flipped the
    // toggle for the entire catalog, so every Power-Buyer row silently
    // switched mode. The toggle is the buyer's explicit, catalog-wide mode
    // control; per-variety memory keeps size/length/qty/color only.
    setQuantity(
      typeof presetBunches === "number" && presetBunches > 0
        ? presetBunches
        : saved?.qty && saved.qty > 0
          ? saved.qty
          : 1,
    );
    // DIRECTIVE #023: respect saved boxCode + universal QB default on
    // popover re-open.
    if (cap) setBoxCode(getDefaultBoxUnit(variety, saved?.boxCode ?? null));
  }, [open, variantIdx, presetBunches, presetLength, presetColor]);
  /* eslint-enable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */

  // Reset boxCode when length changes if current code is no longer offered.
  useEffect(() => {
    if (!cap) return;
    if (boxCode === null || cap[boxCode] === null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- align to new length capacity
      setBoxCode(getDefaultBoxUnit(variety, null));
    }
  }, [cap, boxCode, variety]);

  // ESC closes.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // DIRECTIVE #025: hooks above any early return per rules-of-hooks.
  const priceGate = usePriceGate();
  const canSeePrices = priceGate.ready && priceGate.canSeePrices;
  const router = useRouter();

  // #452: sold-out for the current selection, via the shared cascade.
  const soldOutSet = new Set(soldOutKeys ?? []);
  const selectionSoldOut = isSoldOut(soldOutSet, {
    slug: variety.slug,
    colorChoice: color || null,
    length: isHydrangea ? null : length,
  });
  const blockSoldOut = canSeePrices && selectionSoldOut;

  if (!open) return null;

  const stem = resolveStemPrice(variety, length, color || undefined);
  const stemsPerBunch = variety.stemsPerBunch ?? 0;
  const stemsBoxActive =
    cap && boxCode ? stemsInBox(cap, boxCode) ?? 0 : 0;

  const stems =
    unit === "bunch"
      ? quantity * stemsPerBunch
      : quantity * stemsBoxActive;
  const total = stems * stem;

  const colorPart = color
    ? color === "mix"
      ? " * Mix Box"
      : ` * ${colorLabel(color)}`
    : "";
  const gradePart = variety.grade ? ` ${variety.grade}` : "";

  const summaryUnitLabel =
    unit === "bunch"
      ? `${quantity} ${quantity === 1 ? "bunch" : "bunches"}`
      : `${quantity} ${boxCode ?? "box"}`;
  const summaryLine = `Adding ${summaryUnitLabel} of ${card.name}${gradePart}${colorPart} = ${stems} stems * $${total.toFixed(2)}`;

  const addLabel = (() => {
    if (unit === "box") {
      const sizeLabel = boxCode ?? "box";
      return `Add ${quantity} ${sizeLabel} of ${card.name}${gradePart}${colorPart} to box`;
    }
    const bunchPart = quantity === 1 ? "bunch" : "bunches";
    if (color && color !== "mix") {
      return `Add ${quantity} ${bunchPart} of ${colorLabel(color)} to box`;
    }
    if (color === "mix") {
      return `Add ${quantity} Mix ${bunchPart} of ${card.name} to box`;
    }
    return `Add ${quantity} ${bunchPart} of ${card.name} to box`;
  })();

  function handleAdd() {
    if (priceGate.ready && !priceGate.canSeePrices) {
      router.push(
        `/sign-in?redirect=${encodeURIComponent(`/catalog/${card.routeSlug}`)}`,
      );
      onClose();
      return;
    }
    // #452: a sold-out selection cannot be added.
    if (blockSoldOut) return;
    // Step 6.7.3.1 / AC185: same per-box loop as detail page (commit
    // e8cf668 of 6.7.3). In Box mode, qty > 2 produces a single-line
    // stem count that exceeds even HB capacity; the engine throws and
    // React swallows the error - silent failure. Split into per-box
    // calls. Bunch mode keeps the single-call path.
    const iterations = unit === "box" ? quantity : 1;
    const perIterQty = unit === "box" ? 1 : quantity;
    try {
      let runningCart = readCart();
      for (let i = 0; i < iterations; i++) {
        const outcome = addToBox(
          runningCart,
          {
            variety,
            length: isHydrangea ? undefined : length,
            unit,
            quantity: perIterQty,
            colorChoice: color || undefined,
            boxCode: unit === "box" ? boxCode ?? undefined : undefined,
          },
        );
        if (outcome.kind === "added") {
          runningCart = outcome.cart;
          continue;
        }
        // Mix conflict on the quick-add path: retry with forceNewBox.
        const retry = addToBox(
          runningCart,
          {
            variety,
            length: isHydrangea ? undefined : length,
            unit,
            quantity: perIterQty,
            colorChoice: color || undefined,
            boxCode: unit === "box" ? boxCode ?? undefined : undefined,
          },
          { forceNewBox: true },
        );
        if (retry.kind === "added") {
          runningCart = retry.cart;
        }
      }
      writeCart(runningCart);
      writeVarietyPref(variety.slug, {
        color: color || undefined,
        length: isHydrangea ? undefined : length,
        grade: variety.grade,
        qty: quantity,
        unit,
        // DIRECTIVE #023: persist the resolved box size so a later
        // PowerBuyerRow + popover re-open see the buyer's pick.
        boxCode: boxCode ?? undefined,
      });
      onAdded?.(`Added: ${addLabel.replace(/^Add /, "")}`);
      onClose();
    } catch (err) {
      // Surface the engine throw to the user (extreme Bunch mode
      // quantities can still trip "no box big enough"). Spec section 2
      // "silent failures unacceptable" rule.
      const msg = err instanceof Error ? err.message : String(err);
      onAdded?.(`Couldn't add: ${msg}`);
      onClose();
    }
  }

  return (
    <div
      data-testid="quick-add-popover"
      role="dialog"
      aria-modal="true"
      aria-label={`Add ${card.name} to box`}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 md:items-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[360px] rounded-t-2xl bg-white p-5 shadow-2xl md:w-[320px] md:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-brand-border md:hidden" />
        <h2
          data-testid="quick-add-name"
          className="text-base font-bold text-brand-text"
        >
          {card.name}
          {variety.grade ? (
            <span className="ml-1 text-sm font-medium text-brand-text-muted">
              ({variety.grade})
            </span>
          ) : null}
        </h2>
        {canSeePrices ? (
          <p className="mt-0.5 text-xs text-brand-text-muted">
            ${stem.toFixed(2)} / stem
            {stemsPerBunch > 0 ? ` * ${stemsPerBunch} stems / bunch` : ""}
          </p>
        ) : priceGate.ready ? (
          <p className="mt-0.5 text-xs">
            <SignInForPricesCTA variant="inline" />
          </p>
        ) : null}

        {card.isGrouped && (
          <div className="mt-3">
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-brand-text-muted">
              {strings?.gradeLabel ?? "Grade"}
            </p>
            <div data-testid="quick-add-grade-picker" className="flex flex-wrap gap-1.5">
              {card.variants.map((v, i) => {
                const isActive = i === variantIdx;
                return (
                  <button
                    key={v.slug}
                    type="button"
                    data-testid={`quick-add-grade-${v.grade?.toLowerCase()}`}
                    aria-pressed={isActive}
                    onClick={() => setVariantIdx(i)}
                    className={
                      isActive
                        ? "rounded-md bg-brand-teal px-3 py-1 text-xs font-bold text-white"
                        : "rounded-md border border-brand-border bg-white px-3 py-1 text-xs font-semibold text-brand-text-muted"
                    }
                  >
                    {v.grade}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {hasColorPicker && (
          <div className="mt-3">
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-brand-text-muted">
              {strings?.colorLabel ?? "Color"}
            </p>
            <ColorPicker
              options={variety.colorOptions ?? []}
              active={color}
              allowMix={mixAllowedForUnit(variety, unit)}
              onChange={setColor}
              variant="detail"
              testIdPrefix="quick-add-color"
            />
          </div>
        )}

        {showLengthPicker && (
          <div className="mt-3">
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-brand-text-muted">
              {strings?.lengthLabel ?? "Length"}
            </p>
            <div data-testid="quick-add-length-picker" className="flex gap-1.5">
              {variety.lengths.map((l) => {
                const isActive = l === length;
                // #452: grey this length if it (or the variety/color) is out.
                const chipSoldOut =
                  canSeePrices &&
                  isSoldOut(soldOutSet, {
                    slug: variety.slug,
                    colorChoice: color || null,
                    length: l,
                  });
                return (
                  <button
                    key={l}
                    type="button"
                    data-testid={`quick-add-length-${l}`}
                    data-sold-out={chipSoldOut ? "true" : undefined}
                    aria-pressed={isActive}
                    onClick={() => setLength(l)}
                    className={
                      (isActive
                        ? "rounded-md bg-brand-teal px-3 py-1 text-xs font-bold text-white"
                        : "rounded-md border border-brand-border px-3 py-1 text-xs font-semibold text-brand-text-muted") +
                      (chipSoldOut ? " line-through opacity-40" : "")
                    }
                  >
                    {l} cm
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!isHydrangea && (
          <div className="mt-3">
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-brand-text-muted">
              {strings?.buyBy ?? "Buy by"}
            </p>
            <div
              data-testid="quick-add-unit-toggle"
              className="inline-flex rounded-md border border-brand-border bg-brand-surface p-0.5"
            >
              <button
                type="button"
                data-testid="quick-add-unit-box"
                aria-pressed={unit === "box"}
                onClick={() => setGlobalUnit("box")}
                className={
                  unit === "box"
                    ? "rounded bg-brand-teal px-3 py-1 text-xs font-bold text-white"
                    : "rounded px-3 py-1 text-xs font-semibold text-brand-text-muted"
                }
              >
                {strings?.unitBox ?? "Box"}
              </button>
              <button
                type="button"
                data-testid="quick-add-unit-bunch"
                aria-pressed={unit === "bunch"}
                onClick={() => setGlobalUnit("bunch")}
                className={
                  unit === "bunch"
                    ? "rounded bg-brand-teal px-3 py-1 text-xs font-bold text-white"
                    : "rounded px-3 py-1 text-xs font-semibold text-brand-text-muted"
                }
              >
                {strings?.unitBunch ?? "Bunch"}
              </button>
            </div>
          </div>
        )}

        {(unit === "box" || isHydrangea) && availableBoxCodes.length > 1 && (
          <div className="mt-3">
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-brand-text-muted">
              {strings?.boxSizeLabel ?? "Box size"}
            </p>
            <div
              data-testid="quick-add-box-size-picker"
              className="flex gap-1.5"
            >
              {availableBoxCodes.map((c) => {
                const isActive = c === boxCode;
                return (
                  <button
                    key={c}
                    type="button"
                    data-testid={`quick-add-box-size-${c}`}
                    aria-pressed={isActive}
                    onClick={() => setBoxCode(c)}
                    className={
                      isActive
                        ? "rounded-md bg-brand-teal px-3 py-1 text-xs font-bold text-white"
                        : "rounded-md border border-brand-border px-3 py-1 text-xs font-semibold text-brand-text-muted"
                    }
                  >
                    {c} ({cap?.[c]} stems)
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-3">
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-brand-text-muted">
            {strings?.quantityLabel ?? "Quantity"}
          </p>
          <div className="inline-flex items-center overflow-hidden rounded-md border border-brand-border">
            <button
              type="button"
              data-testid="quick-add-decrement"
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              className="h-11 w-11 bg-brand-surface text-base text-brand-text-muted"
            >
              -
            </button>
            <input
              data-testid="quick-add-qty"
              aria-label="Quantity"
              value={quantity}
              readOnly
              className="h-11 w-12 border-0 text-center text-base font-bold"
            />
            <button
              type="button"
              data-testid="quick-add-increment"
              onClick={() => setQuantity((q) => Math.min(99, q + 1))}
              className="h-11 w-11 bg-brand-surface text-base text-brand-text-muted"
            >
              +
            </button>
          </div>
        </div>

        {canSeePrices && (
          <p
            data-testid="quick-add-summary"
            className="mt-3 rounded-lg bg-brand-teal-soft px-3 py-2 text-xs text-brand-teal-dark"
          >
            {summaryLine}
          </p>
        )}

        <div className="mt-3 flex gap-2">
          <button
            type="button"
            data-testid="quick-add-cancel"
            onClick={onClose}
            className="rounded-lg border border-brand-border bg-white px-4 py-2.5 text-sm font-semibold text-brand-text-muted hover:bg-brand-surface"
          >
            {strings?.cancel ?? "Cancel"}
          </button>
          <button
            type="button"
            data-testid="quick-add-confirm"
            data-auth-gated={
              priceGate.ready && !canSeePrices ? "true" : undefined
            }
            data-sold-out={blockSoldOut ? "true" : undefined}
            disabled={blockSoldOut}
            onClick={handleAdd}
            className={
              "flex-1 rounded-lg px-4 py-2.5 text-sm font-bold shadow-sm " +
              (blockSoldOut
                ? "cursor-not-allowed bg-brand-border text-brand-text-muted"
                : "text-white " +
                  (priceGate.ready && !canSeePrices
                    ? "bg-brand-teal/70 hover:bg-brand-teal"
                    : "bg-brand-teal hover:bg-brand-teal/90"))
            }
          >
            {blockSoldOut
              ? "Sold out"
              : priceGate.ready && !canSeePrices
                ? "Sign in to add"
                : addLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
